/**
 * Auto-suggest multi-participant "cases" from a set of recordings by
 * clustering them on wall-clock proximity (MULTI-PARTICIPANT-FOCUS).
 *
 * A case is a clinical event several people recorded around the same time.
 * Because the explicit case-linkage fields aren't populated, concurrency is
 * inferred: two recordings belong together when their [start, end] windows
 * are within `marginMinutes` of each other (overlap = zero gap; a small
 * margin absorbs the start/stop scatter of separately-worn sensors).
 * Overlap is transitive — a relay of overlapping segments is one case — so
 * we union-find the pairs into clusters, then keep clusters that span at
 * least two distinct participants.
 *
 * Pure + framework-free so it's unit-testable and reusable; the caller
 * pre-filters to one procedure and maps its rows onto {@link RecordingWindow}.
 */

export interface RecordingWindow {
  id: string;
  /** Grouping key for "distinct participant" — a user id or display label. */
  participant: string;
  /** ISO start timestamp. */
  startedAt: string;
  /** ISO end timestamp; null/absent → treated as instantaneous at start. */
  endedAt?: string | null;
}

export interface SuggestedCase {
  /** Member recording ids, ordered by start time. */
  recordingIds: string[];
  /** Distinct participant keys, in first-seen (start) order. */
  participants: string[];
  /** Earliest start across the case (ISO). */
  startedAt: string;
  /** Latest end across the case (ISO). */
  endedAt: string;
}

interface Span {
  id: string;
  participant: string;
  start: number;
  end: number;
  startIso: string;
  endIso: string;
}

function toSpan(r: RecordingWindow): Span | null {
  const start = Date.parse(r.startedAt);
  const endIso = r.endedAt ?? r.startedAt;
  const end = Date.parse(endIso);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return {
    id: r.id,
    participant: r.participant,
    start,
    end: Math.max(end, start),
    startIso: r.startedAt,
    endIso,
  };
}

/** Two spans belong to the same case when neither starts more than `marginMs`
 *  after the other ends — i.e. they overlap or the gap is within the margin. */
function linked(a: Span, b: Span, marginMs: number): boolean {
  return b.start <= a.end + marginMs && a.start <= b.end + marginMs;
}

class UnionFind {
  private parent: number[];
  constructor(n: number) {
    this.parent = Array.from({ length: n }, (_, i) => i);
  }
  find(x: number): number {
    while (this.parent[x] !== x) {
      this.parent[x] = this.parent[this.parent[x]];
      x = this.parent[x];
    }
    return x;
  }
  union(a: number, b: number): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent[ra] = rb;
  }
}

/**
 * Cluster recordings into candidate multi-participant cases. Returns clusters
 * with >= 2 distinct participants, ordered earliest-first; recordings and
 * participants inside each are ordered by start time.
 */
export function suggestConcurrentCases(
  recordings: RecordingWindow[],
  marginMinutes: number,
): SuggestedCase[] {
  const marginMs = Math.max(0, marginMinutes) * 60_000;
  const spans = recordings
    .map(toSpan)
    .filter((s): s is Span => s !== null)
    .sort((a, b) => a.start - b.start);

  const uf = new UnionFind(spans.length);
  for (let i = 0; i < spans.length; i++) {
    // Spans are start-sorted: once b starts beyond a's end + margin, no later
    // b can link to a either, so stop scanning this i.
    for (let j = i + 1; j < spans.length; j++) {
      if (spans[j].start > spans[i].end + marginMs) break;
      if (linked(spans[i], spans[j], marginMs)) uf.union(i, j);
    }
  }

  const byRoot = new Map<number, Span[]>();
  spans.forEach((s, i) => {
    const root = uf.find(i);
    const list = byRoot.get(root);
    if (list) list.push(s);
    else byRoot.set(root, [s]);
  });

  const cases: SuggestedCase[] = [];
  for (const members of byRoot.values()) {
    const participants: string[] = [];
    for (const m of members) {
      if (!participants.includes(m.participant)) participants.push(m.participant);
    }
    if (participants.length < 2) continue; // not a multi-participant case
    cases.push({
      recordingIds: members.map((m) => m.id),
      participants,
      startedAt: members.reduce((a, m) => (m.start < Date.parse(a) ? m.startIso : a), members[0].startIso),
      endedAt: members.reduce((a, m) => (m.end > Date.parse(a) ? m.endIso : a), members[0].endIso),
    });
  }

  cases.sort((a, b) => Date.parse(a.startedAt) - Date.parse(b.startedAt));
  return cases;
}

/** True when any two of the given windows overlap in wall-clock time (strict —
 *  no margin). Used for the selected-set "times overlap" indicator. */
export function windowsOverlap(recordings: RecordingWindow[]): boolean {
  const spans = recordings
    .map(toSpan)
    .filter((s): s is Span => s !== null)
    .sort((a, b) => a.start - b.start);
  if (spans.length < 2) return false;
  let maxEnd = spans[0].end;
  for (let i = 1; i < spans.length; i++) {
    if (spans[i].start < maxEnd) return true;
    maxEnd = Math.max(maxEnd, spans[i].end);
  }
  return false;
}
